export * from './dto_chat_message_type';
import { z } from 'zod';
/** 0:project, 1:content, 2:folder */
export const DtoType = {
  project: 0,
  content: 1,
  folder: 2,
} as const;
export const DtoTypeReverse = {
  0: 'project',
  1: 'content',
  2: 'folder',
} as const;
export type DtoType = (typeof DtoType)[keyof typeof DtoType];
export type DtoTypeReverse =
  (typeof DtoTypeReverse)[keyof typeof DtoTypeReverse];

/** 0:folder, 1:content, 2:project */
export const DtoFolderItemType = {
  folder: 0,
  content: 1,
  project: 2,
} as const;

/** 0:ROOT, 1:PROJECT, 2:USER_ASSET, 3:SCRIPT, 4:VIDEO, 5:CHANNEL, 99:CUSTOM */
export const DtoFolderType = {
  ROOT: 0,
  PROJECT: 1,
  USER_ASSET: 2,
  SCRIPT: 3,
  VIDEO: 4,
  CHANNEL: 5,
  CUSTOM: 99,
} as const;
export const DtoFolderTypeReverse = {
  0: 'ROOT',
  1: 'PROJECT',
  2: 'USER_ASSET',
  3: 'SCRIPT',
  4: 'VIDEO',
  5: 'CHANNEL',
  99: 'CUSTOM',
} as const;
export type DtoFolderType = (typeof DtoFolderType)[keyof typeof DtoFolderType];
export type DtoFolderTypeReverse =
  (typeof DtoFolderTypeReverse)[keyof typeof DtoFolderTypeReverse];

/**
 * 파일 업로드/이동이 허용되는 폴더 타입
 * ROOT, USER_ASSET만 허용
 */
export const UPLOADABLE_FOLDER_TYPES = [
  DtoFolderType.ROOT,
  DtoFolderType.USER_ASSET,
] as const;
export const DtoFolderItemTypeReverse = {
  0: 'folder',
  1: 'content',
  2: 'project',
} as const;
export type DtoFolderItemType =
  (typeof DtoFolderItemType)[keyof typeof DtoFolderItemType];
export type DtoFolderItemTypeReverse =
  (typeof DtoFolderItemTypeReverse)[keyof typeof DtoFolderItemTypeReverse];

/** 1:video, 2:image, 3:audio, 4:font */
export const DtoContentType = {
  video: 1,
  image: 2,
  audio: 3,
  font: 4,
  document: 5,
} as const;
export const DtoContentTypeReverse = {
  1: 'video',
  2: 'image',
  3: 'audio',
  4: 'font',
  5: 'document',
} as const;
export type DtoContentType =
  (typeof DtoContentType)[keyof typeof DtoContentType];
export type DtoContentTypeReverse =
  (typeof DtoContentTypeReverse)[keyof typeof DtoContentTypeReverse];

/** Backend FileExtension enum codes */
export const DtoExtension = {
  mp4: 0,
  mov: 1,
  avi: 2,
  mkv: 3,
  webm: 6,
  mxf: 8,
  mp3: 10,
  wav: 11,
  aac: 12,
  jpeg: 20,
  png: 21,
  jpg: 22,
  gif: 23,
  webp: 25,
  ttf: 30,
  otf: 31,
  woff: 32,
  woff2: 33,
  pdf: 40,
  hwpx: 44,
  hwp: 45,
  pptx: 48,
  ts: 100,
} as const;
export const DtoExtensionReverse = {
  0: 'mp4',
  1: 'mov',
  2: 'avi',
  3: 'mkv',
  6: 'webm',
  8: 'mxf',
  10: 'mp3',
  11: 'wav',
  12: 'aac',
  20: 'jpeg',
  21: 'png',
  22: 'jpg',
  23: 'gif',
  25: 'webp',
  30: 'ttf',
  31: 'otf',
  32: 'woff',
  33: 'woff2',
  40: 'pdf',
  44: 'hwpx',
  45: 'hwp',
  48: 'pptx',
  100: 'ts',
} as const;
export type DtoExtension = (typeof DtoExtension)[keyof typeof DtoExtension];
export type DtoExtensionReverse =
  (typeof DtoExtensionReverse)[keyof typeof DtoExtensionReverse];

/** FileExtension 코드 (Backend FileExtension enum과 일치) */
export const DtoFileExtension = {
  // VIDEO
  mp4: 0,
  mov: 1,
  avi: 2,
  mkv: 3,
  webm: 6,
  mxf: 8,
  ts: 100,
  // AUDIO
  mp3: 10,
  wav: 11,
  aac: 12,
  // IMAGE
  jpeg: 20,
  png: 21,
  jpg: 22,
  gif: 23,
  webp: 25,
  // FONT
  ttf: 30,
  otf: 31,
  woff: 32,
  woff2: 33,
} as const;
export const DtoFileExtensionReverse = {
  // VIDEO
  0: 'mp4',
  1: 'mov',
  2: 'avi',
  3: 'mkv',
  6: 'webm',
  8: 'mxf',
  100: 'ts',
  // AUDIO
  10: 'mp3',
  11: 'wav',
  12: 'aac',
  // IMAGE
  20: 'jpeg',
  21: 'png',
  22: 'jpg',
  23: 'gif',
  25: 'webp',
  // FONT
  30: 'ttf',
  31: 'otf',
  32: 'woff',
  33: 'woff2',
} as const;
export type DtoFileExtension =
  (typeof DtoFileExtension)[keyof typeof DtoFileExtension];
export type DtoFileExtensionReverse =
  (typeof DtoFileExtensionReverse)[keyof typeof DtoFileExtensionReverse];

/** 0: auto, 1: SD(480P), 2: HD(720P), 3: FHD(1080P), 4: QHD(2K), 5: UHD(4K) */
export const DtoResolution = {
  auto: 0,
  'SD(480p)': 1,
  'HD(720p)': 2,
  'FHD(1080p)': 3,
  'QHD(2k)': 4,
  'UHD(4k)': 5,
} as const;
export const DtoResolutionReverse = {
  0: 'auto',
  1: 'SD(480p)',
  2: 'HD(720p)',
  3: 'FHD(1080p)',
  4: 'QHD(2k)',
  5: 'UHD(4k)',
} as const;
export type DtoResolution = (typeof DtoResolution)[keyof typeof DtoResolution];
export type DtoResolutionReverse =
  (typeof DtoResolutionReverse)[keyof typeof DtoResolutionReverse];

/** 0: local, 1: youtube */
export const DtoProviderType = {
  local: 0,
  youtube: 1,
  encoder: 2,
} as const;
export const DtoProviderTypeReverse = {
  0: 'local',
  1: 'youtube',
  2: 'encoder',
  3: 'tts',
} as const;
export type DtoProviderType =
  (typeof DtoProviderType)[keyof typeof DtoProviderType];
export type DtoProviderTypeReverse =
  (typeof DtoProviderTypeReverse)[keyof typeof DtoProviderTypeReverse];

/** 1: email, 2: google, 3: kakao, 4: naver, 5: apple */
export const DtoAuthProviderType = {
  email: 1,
  google: 2,
  kakao: 3,
  naver: 4,
  apple: 5,
} as const;
export const DtoAuthProviderTypeReverse = {
  1: 'email',
  2: 'google',
  3: 'kakao',
  4: 'naver',
  5: 'apple',
} as const;
export type DtoAuthProviderType =
  (typeof DtoAuthProviderType)[keyof typeof DtoAuthProviderType];
export type DtoAuthProviderTypeReverse =
  (typeof DtoAuthProviderTypeReverse)[keyof typeof DtoAuthProviderTypeReverse];

/** 0: 0 - 30s, 1: 30s - 1m, 2: 1m - 1m 30s, 3: 1m 30s - 3m */
export const DtoOutputLength = {
  '0-30s': 1,
  '30s - 1m': 2,
  '1m - 1m 30s': 3,
  '1m 30s - 3m': 4,
} as const;
export const DtoOutputLengthReverse = {
  1: '0-30s',
  2: '30s - 1m',
  3: '1m - 1m 30s',
  4: '1m 30s - 3m',
} as const;
export type DtoOutputLength =
  (typeof DtoOutputLength)[keyof typeof DtoOutputLength];
export type DtoOutputLengthReverse =
  (typeof DtoOutputLengthReverse)[keyof typeof DtoOutputLengthReverse];

/** 1: 9:16, 2: 1:1, 3: 16:9 */
export const DtoAspectRatio = {
  '9:16': 1,
  '1:1': 2,
  '16:9': 3,
} as const;
export const DtoAspectRatioReverse = {
  1: '9:16',
  2: '1:1',
  3: '16:9',
} as const;
export type DtoAspectRatio =
  (typeof DtoAspectRatio)[keyof typeof DtoAspectRatio];
export type DtoAspectRatioReverse =
  (typeof DtoAspectRatioReverse)[keyof typeof DtoAspectRatioReverse];

/** 0: 알 수 없음, 1: 진행 중, 2: 완료, 3: 오류 발생 */
export const DtoStatus = {
  unknown: 0,
  progressing: 1,
  completed: 2,
  error: 3,
} as const;
export const DtoStatusReverse = {
  0: 'unknown',
  1: 'progressing',
  2: 'completed',
  3: 'error',
} as const;
export type DtoStatus = (typeof DtoStatus)[keyof typeof DtoStatus];
export type DtoStatusReverse =
  (typeof DtoStatusReverse)[keyof typeof DtoStatusReverse];

/** 0: 업로드 미완료, 1: 활성, 2: 삭제, 3: 정지, 4: 오류 */
export const DtoContentStatus = {
  'not-uploaded': 0,
  active: 1,
  deleted: 2,
  stopped: 3,
  error: 4,
} as const;
export const DtoContentStatusReverse = {
  0: 'not-uploaded',
  1: 'active',
  2: 'deleted',
  3: 'stopped',
  4: 'error',
} as const;
export type DtoContentStatus =
  (typeof DtoContentStatus)[keyof typeof DtoContentStatus];
export type DtoContentStatusReverse =
  (typeof DtoContentStatusReverse)[keyof typeof DtoContentStatusReverse];

/**
 * 0: 준비중, 1: 활성, 2: 삭제, 3:정지, 4:오류
 * v2.0: 10: 컨텐츠 준비, 20: 인덱싱, 30: 임베딩, 70: 분할, 80: 스토리 생성, 90: 대본 생성, 100: 웰컴메시지
 */
export const DtoProjectStatus = {
  'not-ready': 0,
  activate: 1,
  deleted: 2,
  stopped: 3,
  error: 4,
  // v2.0 진행 상태
  prepareContents: 10,
  indexing: 20,
  embedding: 30,
  splitting: 70,
  storyGenerating: 80,
  scriptGenerating: 90,
  welcomeMessage: 100,
  duplicating: 110,
} as const;
export const DtoProjectStatusReverse = {
  0: 'not-ready',
  1: 'activate',
  2: 'deleted',
  3: 'stopped',
  4: 'error',
  // v2.0 진행 상태
  10: 'prepareContents',
  20: 'indexing',
  30: 'embedding',
  70: 'splitting',
  80: 'storyGenerating',
  90: 'scriptGenerating',
  100: 'welcomeMessage',
  110: 'duplicating',
} as const;
export type DtoProjectStatus =
  (typeof DtoProjectStatus)[keyof typeof DtoProjectStatus];
export type DtoProjectStatusReverse =
  (typeof DtoProjectStatusReverse)[keyof typeof DtoProjectStatusReverse];

/** 1: 광고 및 마케팅 */
export const DtoAccountConsent = {
  'advertising-and-marketing': 1,
} as const;
export const DtoAccountConsentReverse = {
  1: 'advertising-and-marketing',
} as const;
export type DtoAccountConsent =
  (typeof DtoAccountConsent)[keyof typeof DtoAccountConsent];
export type DtoAccountConsentReverse =
  (typeof DtoAccountConsentReverse)[keyof typeof DtoAccountConsentReverse];

/** 1: sign-in, 2: reset password, 3: account link */
export const DtoVerificationType = {
  'sign-in': 1,
  'reset-password': 2,
  'account-link': 3,
} as const;
export const DtoVerificationTypeReverse = {
  1: 'sign-in',
  2: 'reset-password',
  3: 'account-link',
} as const;
export type DtoVerificationType =
  (typeof DtoVerificationType)[keyof typeof DtoVerificationType];
export type DtoVerificationTypeReverse =
  (typeof DtoVerificationTypeReverse)[keyof typeof DtoVerificationTypeReverse];

export const DtoProjectType = {
  highlight: 0,
  spotlight: 1,
  search: 2,
  ssul: 3,
  commerce: 4,
  generateToShort: 6,
  gtsChat: 7,
  sourceToContent: 8,
  gtsStep: 9,
  remotionChat: 10,
  aiVideoGenerate: 11,
  vibemotion: 12,
  weatherCaster: 13,
  shinhan: 14,
  hyperframes: 15,
  soccer: 16,
  broadcastVault: 17,
  bulkShortform: 18,
  remotionDeck: 19,
  ttsMaker: 20,
  b2bPlayground: 1000,
  b2bWeatherCast: 1001,
  b2bAvatar: 1002,
  agentAvatar: 10000,
  agentPreset: 10001,
  agentStoryboard: 10002,
  agentWeatherCast: 10003,
  agentAvatarPreset: 10004,
  agentScriptImageGts: 10005,
  agentScriptAvatar: 10006,
  agentVideoDubbing: 10007,
  agentPdfInfographic: 10008,
  agentStoryboardInfographic: 10009,
  agentPdfVerbatim: 10010,
  agentImageAnimate: 10011,
  agentVideoCaption: 10012,
  agentSsulShorts: 10013,
  agentSsulStory: 10022,
  agentWritingGuide: 10014,
  agentVisangWordIllustrationGif: 10015,
  agentIts: 10016,
  agentRemotionDeck: 10017,
  agentChoreography: 10018,
  agentWordChant: 10019,
  agentCardNews: 10020,
  agentSsulStudio: 10025,
  agentLectureVideo: 10021,
  agentSsulDeck: 10023,
  agentRemotionDeckScript: 10024,
  agentAnalysisStoryboard: 10026,
  agentAudioDocInfographic: 10027,
  agentPdfStory: 10028,
  agentCinematic: 10030,
  agentLectureDeck: 10031,
  agentLiquorIntro: 10032,
  agentRemotionDeckVideoCreator: 10033,
  agentSlideLecture: 10034,
  agentMarketingAsset: 10035,
  agentStoryIllustrationAnimationV2: 10036,
} as const;
export const DtoProjectTypeReverse = {
  0: 'highlight',
  1: 'spotlight',
  2: 'search',
  3: 'ssul',
  4: 'commerce',
  6: 'generateToShort',
  7: 'gtsChat',
  8: 'sourceToContent',
  9: 'gtsStep',
  10: 'remotionChat',
  11: 'aiVideoGenerate',
  12: 'vibemotion',
  13: 'weatherCaster',
  14: 'shinhan',
  15: 'hyperframes',
  16: 'soccer',
  17: 'broadcastVault',
  18: 'bulkShortform',
  19: 'remotionDeck',
  20: 'ttsMaker',
  1000: 'b2bPlayground',
  1001: 'b2bWeatherCast',
  1002: 'b2bAvatar',
  10000: 'agentAvatar',
  10001: 'agentPreset',
  10002: 'agentStoryboard',
  10003: 'agentWeatherCast',
  10004: 'agentAvatarPreset',
  10005: 'agentScriptImageGts',
  10006: 'agentScriptAvatar',
  10007: 'agentVideoDubbing',
  10008: 'agentPdfInfographic',
  10009: 'agentStoryboardInfographic',
  10010: 'agentPdfVerbatim',
  10011: 'agentImageAnimate',
  10012: 'agentVideoCaption',
  10013: 'agentSsulShorts',
  10022: 'agentSsulStory',
  10014: 'agentWritingGuide',
  10015: 'agentVisangWordIllustrationGif',
  10016: 'agentIts',
  10017: 'agentRemotionDeck',
  10018: 'agentChoreography',
  10019: 'agentWordChant',
  10020: 'agentCardNews',
  10025: 'agentSsulStudio',
  10021: 'agentLectureVideo',
  10023: 'agentSsulDeck',
  10024: 'agentRemotionDeckScript',
  10026: 'agentAnalysisStoryboard',
  10027: 'agentAudioDocInfographic',
  10028: 'agentPdfStory',
  10030: 'agentCinematic',
  10031: 'agentLectureDeck',
  10032: 'agentLiquorIntro',
  10033: 'agentRemotionDeckVideoCreator',
  10034: 'agentSlideLecture',
  10035: 'agentMarketingAsset',
  10036: 'agentStoryIllustrationAnimationV2',
} as const;
export type DtoProjectType =
  (typeof DtoProjectType)[keyof typeof DtoProjectType];
export type DtoProjectTypeReverse =
  (typeof DtoProjectTypeReverse)[keyof typeof DtoProjectTypeReverse];

/** 0: 대기, 1: 진행 중, 2: 완료, 3: 실패, 4: 취소, 5: 삭제 */
export const DtoRenderingHistoryStatusType = {
  pending: 0,
  progressing: 1,
  completed: 2,
  failed: 3,
  canceled: 4,
  deleted: 5,
  suspended: 6,
} as const;
export const DtoRenderingHistoryStatusTypeReverse = {
  0: 'pending',
  1: 'progressing',
  2: 'completed',
  3: 'failed',
  4: 'canceled',
  5: 'deleted',
  6: 'suspended',
} as const;
export type DtoRenderingHistoryStatusType =
  (typeof DtoRenderingHistoryStatusType)[keyof typeof DtoRenderingHistoryStatusType];
export type DtoRenderingHistoryStatusTypeReverse =
  (typeof DtoRenderingHistoryStatusTypeReverse)[keyof typeof DtoRenderingHistoryStatusTypeReverse];

/** 0: (FE) output, 1: (FE) editor, 2: (FE) desktop, 3: weather-caster, 4: hyperframes, 5: bulk-shortform */
export const DtoVideoRenderingHistoryType = {
  output: 0,
  editor: 1,
  desktop: 2,
  weatherCaster: 3,
  hyperframes: 4,
  bulkShortform: 5,
} as const;
export const DtoVideoRenderingHistoryTypeReverse = {
  0: 'output',
  1: 'editor',
  2: 'desktop',
  3: 'weatherCaster',
  4: 'hyperframes',
  5: 'bulkShortform',
} as const;
export type DtoVideoRenderingHistoryType =
  (typeof DtoVideoRenderingHistoryType)[keyof typeof DtoVideoRenderingHistoryType];
export type DtoVideoRenderingHistoryTypeReverse =
  (typeof DtoVideoRenderingHistoryTypeReverse)[keyof typeof DtoVideoRenderingHistoryTypeReverse];

/** 0: RENDERING, 1: UPLOADING, 2: COMPLETED, 3: RENDERING_FAILED, 4: UPLOAD_FAILED */
export const DtoVideoRenderingProgressStatusType = {
  rendering: 0,
  uploading: 1,
  completed: 2,
  renderingFailed: 3,
  uploadFailed: 4,
} as const;
export const DtoVideoRenderingProgressStatusTypeReverse = {
  0: 'rendering',
  1: 'uploading',
  2: 'completed',
  3: 'renderingFailed',
  4: 'uploadFailed',
} as const;
export type DtoVideoRenderingProgressStatusType =
  (typeof DtoVideoRenderingProgressStatusType)[keyof typeof DtoVideoRenderingProgressStatusType];
export type DtoVideoRenderingProgressStatusTypeReverse =
  (typeof DtoVideoRenderingProgressStatusTypeReverse)[keyof typeof DtoVideoRenderingProgressStatusTypeReverse];

/** 0: korean, 1: english, 2: japanese */
export const DtoLanguage = {
  korean: 0,
  english: 1,
  japanese: 2,
} as const;
export const DtoLanguageReverse = {
  0: 'korean',
  1: 'english',
  2: 'japanese',
} as const;
export type DtoLanguage = (typeof DtoLanguage)[keyof typeof DtoLanguage];
export type DtoLanguageReverse =
  (typeof DtoLanguageReverse)[keyof typeof DtoLanguageReverse];

export const DtoPointHistoryType = {
  ADD_FREE_PLAN_BY_ADMIN: 101,
  ADD_PAID_PLAN_BY_ADMIN: 102,
  ADD_PURCHASED_POINT_BY_ADMIN: 103,
  ADD_PROMOTION_POINT_BY_ADMIN: 104,
  ADD_BY_FREE_PLAN: 201,
  ADD_BY_PAID_PLAN: 202,
  ADD_PURCHASED_POINT: 203,
  ADD_PROMOTION_POINT: 204,
  ADD_PAYBACK_RENDERING: 205,
  ADD_BY_REFERRAL: 206,
  ADD_PROMOTION_POINT_AUTO_GRANT: 207,
  CONSUMED_BY_ADMIN: 300,
  CONSUMED_LTS_ANALYSIS_VIDEOS: 301,
  CONSUMED_LTS_CHAT: 302,
  CONSUMED_LTS_SEARCHING: 303,
  CONSUMED_LTS_GENERATE_SHORTS: 304,
  CONSUMED_LTS_RENDERING: 305,
  CONSUMED_LTS_GENERATE_TITLE: 306,
  CONSUMED_MTS_ANALYSIS_VIDEOS: 307,
  CONSUMED_MTS_SCRIPT: 308,
  CONSUMED_MTS_GENERATE_VOICE: 309,
  CONSUMED_MTS_GENERATE_SHORTS: 310,
  CONSUMED_MTS_CHAT: 311,
  CONSUMED_MTS_RENDERING: 312,
  CONSUMED_MTS_GENERATE_TITLE: 313,
  CONSUMED_IMAGE_EDITOR_GENERATE: 314,
  CONSUMED_ZERO: 315,
  CONSUMED_INSIGHT_THUMBNAIL: 316,
  CONSUMED_INSIGHT_TITLE: 317,
  CONSUMED_INSIGHT_VIDEO_STRUCTURE: 318,
  CONSUMED_INSIGHT_VIDEO_COMPARISON: 319,
  CONSUMED_INSIGHT_COMMENT: 320,
  CONSUMED_INSIGHT_KEYWORD_TRANSLATE: 321,
  CONSUMED_INSIGHT_KEYWORD_RECOMMEND: 322,
  CONSUMED_INSIGHT_SCRIPT_EXTRACT: 323,
  CONSUMED_GTS_VIDEO_GENERATE: 324,
  CONSUMED_DUPLICATE_PROJECT: 325,
  CONSUMED_INSIGHT_SEARCH: 326,
  CONSUMED_INSIGHT_VIDEO_DETAIL: 327,
  CONSUMED_INSIGHT_CHANNEL: 328,
  CONSUMED_INSIGHT_SHORTS_PERFORMANCE: 329,
  CONSUMED_INSIGHT_SIMILAR_SHORTS: 330,
  CONSUMED_GPT_IMAGE_2_GENERATE: 331,
  CONSUMED_INFO_VIDEO_GENERATE: 332,
  CONSUMED_CARD_NEWS_GENERATE: 333,
  CONSUMED_DECK_VIDEO_GENERATE: 334,
  CONSUMED_TTS_MAKER_GENERATE_VOICE: 335,
  CONSUMED_SSUL_DECK_VIDEO_GENERATE: 336,
  CONSUMED_LECTURE_VIDEO_GENERATE: 337,
  CONSUMED_SLIDE_LECTURE_GENERATE: 339,
  EXPIRED: 400,
  REFUND: 401,
  ROLLBACK_FOR_ADMIN: 500,
  ROLLBACK_FOR_LTS_ANALYSIS_VIDEOS: 501,
  ROLLBACK_LTS_CHAT: 502,
  ROLLBACK_LTS_SEARCHING: 503,
  ROLLBACK_LTS_GENERATE_SHORTS: 504,
  ROLLBACK_LTS_RENDERING: 505,
  ROLLBACK_LTS_GENERATE_TITLE: 506,
  ROLLBACK_MTS_ANALYSIS_VIDEOS: 507,
  ROLLBACK_MTS_SCRIPT: 508,
  ROLLBACK_MTS_GENERATE_VOICE: 509,
  ROLLBACK_MTS_GENERATE_SHORTS: 510,
  ROLLBACK_MTS_CHAT: 511,
  ROLLBACK_MTS_RENDERING: 512,
  ROLLBACK_MTS_GENERATE_TITLE: 513,
  ROLLBACK_IMAGE_EDITOR_GENERATE: 514,
  ROLLBACK_ZERO: 515,
  ROLLBACK_INSIGHT_THUMBNAIL: 516,
  ROLLBACK_INSIGHT_TITLE: 517,
  ROLLBACK_INSIGHT_VIDEO_STRUCTURE: 518,
  ROLLBACK_INSIGHT_VIDEO_COMPARISON: 519,
  ROLLBACK_INSIGHT_COMMENT: 520,
  ROLLBACK_INSIGHT_KEYWORD_TRANSLATE: 521,
  ROLLBACK_INSIGHT_KEYWORD_RECOMMEND: 522,
  ROLLBACK_INSIGHT_SCRIPT_EXTRACT: 523,
  ROLLBACK_GTS_VIDEO_GENERATE: 524,
  ROLLBACK_DUPLICATE_PROJECT: 525,
  ROLLBACK_INSIGHT_SEARCH: 526,
  ROLLBACK_INSIGHT_VIDEO_DETAIL: 527,
  ROLLBACK_INSIGHT_CHANNEL: 528,
  ROLLBACK_INSIGHT_SHORTS_PERFORMANCE: 529,
  ROLLBACK_INSIGHT_SIMILAR_SHORTS: 530,
  ROLLBACK_GPT_IMAGE_2_GENERATE: 531,
  ROLLBACK_INFO_VIDEO_GENERATE: 532,
  ROLLBACK_CARD_NEWS_GENERATE: 533,
  ROLLBACK_DECK_VIDEO_GENERATE: 534,
  ROLLBACK_TTS_MAKER_GENERATE_VOICE: 535,
  ROLLBACK_SSUL_DECK_VIDEO_GENERATE: 536,
  ROLLBACK_LECTURE_VIDEO_GENERATE: 537,
  ROLLBACK_SLIDE_LECTURE_GENERATE: 539,
} as const;

export const DtoPointHistoryTypeReverse = {
  101: '무료 플랜 코인 추가 (관리자)',
  102: '유료 플랜 코인 추가 (관리자)',
  103: '유료 구매 코인 추가 (관리자)',
  104: '프로모션 코인 추가 (관리자)',
  201: '무료 플랜 코인 충전',
  202: '유료 플랜 코인 충전',
  203: '유료 구매 코인 충전',
  204: '프로모션 코인 충전',
  205: '내보내기 오류 코인 환급',
  206: '친구 추천 코인',
  207: '보너스 코인',
  300: '관리자에 의한 소멸',
  301: '프로젝트 생성 (하이라이트 만들기)',
  302: 'AI 요청',
  303: '영상 검색',
  304: '영상 제작',
  305: '내보내기',
  306: '영상 제목 생성',
  307: '프로젝트 생성 (대본으로 만들기)',
  308: '대본 작성',
  309: 'AI 보이스 생성',
  310: '영상 제작',
  311: 'AI 요청',
  312: '내보내기',
  313: '영상 제목 생성',
  314: '이미지 생성',
  315: '포인트 차감 없음',
  316: '썸네일 분석',
  317: '제목 분석',
  318: '영상 구조 분석',
  319: '영상 비교',
  320: '댓글 인사이트',
  321: '키워드 번역',
  322: '키워드 추천',
  323: '스크립트 추출',
  324: '영상 생성',
  325: '프로젝트 복제',
  326: '키워드 검색',
  327: '영상 상세 분석',
  328: '채널 분석',
  329: 'Shorts 성과 예측',
  330: '유사 Shorts 추천',
  331: '이미지 생성',
  332: '인포 영상 생성',
  333: '카드뉴스 만들기',
  334: '덱 영상 생성',
  335: 'TTS 메이커 음성 생성',
  336: '썰 영상 생성',
  337: '강의 영상 생성',
  339: '슬라이드 강의 영상 생성',
  400: '기한 만료',
  401: '환불',
  500: '관리자에 의한 복구',
  501: '프로젝트 생성 복구',
  502: 'AI 요청 복구',
  503: '영상 검색 복구',
  504: '영상 제작 복구',
  505: '내보내기 복구',
  506: '영상 제목 생성 복구',
  507: '프로젝트 생성 복구',
  508: '대본 작성 복구',
  509: 'AI 보이스 생성 복구',
  510: '영상 제작 복구',
  511: 'AI 요청 복구',
  512: '내보내기 복구',
  513: '영상 제목 생성 복구',
  514: '이미지 생성 복구',
  515: '포인트 차감 없음 복구',
  516: '썸네일 분석 복구',
  517: '제목 분석 복구',
  518: '영상 구조 분석 복구',
  519: '영상 비교 복구',
  520: '댓글 인사이트 복구',
  521: '키워드 번역 복구',
  522: '키워드 추천 복구',
  523: '스크립트 추출 복구',
  524: '영상 생성 복구',
  525: '프로젝트 복제 복구',
  526: '키워드 검색 복구',
  527: '영상 상세 분석 복구',
  528: '채널 분석 복구',
  529: 'Shorts 성과 예측 복구',
  530: '유사 Shorts 추천 복구',
  531: '이미지 생성 복구',
  532: '인포 영상 생성 복구',
  533: '카드뉴스 만들기 복구',
  534: '덱 영상 생성 복구',
  535: 'TTS 메이커 음성 생성 복구',
  536: '썰 영상 생성 복구',
  539: '슬라이드 강의 영상 생성 복구',
} as const;

export type DtoPointHistoryType =
  (typeof DtoPointHistoryType)[keyof typeof DtoPointHistoryType];
export type DtoPointHistoryTypeReverse =
  (typeof DtoPointHistoryTypeReverse)[keyof typeof DtoPointHistoryTypeReverse];

export const DtoPointHistoryFindType = {
  charge: 1,
  consume: 2,
  expire: 3,
} as const;

export const DtoPointHistoryFindTypeReverse = {
  1: 'charge',
  2: 'consume',
  3: 'expire',
} as const;

export type DtoPointHistoryFindType =
  (typeof DtoPointHistoryFindType)[keyof typeof DtoPointHistoryFindType];
export type DtoPointHistoryFindTypeReverse =
  (typeof DtoPointHistoryFindTypeReverse)[keyof typeof DtoPointHistoryFindTypeReverse];

export const ProductBillingCycleType = {
  OneTime: 0,
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Annual: 4,
} as const;
export type ProductBillingCycleType =
  (typeof ProductBillingCycleType)[keyof typeof ProductBillingCycleType];
export const ProductBillingCycleReverse = {
  0: 'OneTime',
  1: 'Daily',
  2: 'Weekly',
  3: 'Monthly',
  4: 'Annual',
} as const;
export type ProductBillingCycleReverse =
  (typeof ProductBillingCycleReverse)[keyof typeof ProductBillingCycleReverse];

// 플랜 ID ↔ 표시 이름 매핑
export const PLAN_NAMES = {
  1: 'Free',
  2: 'Starter',
  3: 'Pro',
  4: 'Ultra',
  5: 'Enterprise',
} as const;
export type PlanId = keyof typeof PLAN_NAMES;
export type PlanDisplayName = (typeof PLAN_NAMES)[PlanId];

export const ChatLikeStatusType = {
  // 선택 안함 (Not selected)
  NotSelected: 0,
  // 좋아요 (Like)
  Like: 1,
  // 싫어요 (Dislike)
  Dislike: 2,
} as const;

export type ChatLikeStatusType =
  (typeof ChatLikeStatusType)[keyof typeof ChatLikeStatusType];

/**
 * 프로젝트 에러 코드
 * - AI에서 전달 받는 에러: 1000~1007
 * - BE 레벨 에러: 1101~1116
 * - v2.0 AI 생성 에러: 1120~1125
 * - 기타: 1151, 2000
 */
export const DtoProjectErrorType = {
  // AI에서 전달 받는 에러
  UNKNOWN_ERROR: 1000,
  VALIDATION_ERROR: 1001,
  AUTH_ERROR: 1002,
  NOT_FOUND: 1003,
  NO_DATA: 1004,
  INDEX_DATA_NOT_FOUND: 1005,
  YOUTUBE_DOWNLOAD_ERROR: 1006,
  NO_STT_DATA: 1007,
  NO_AUDIO_DATA: 1009,
  // BE 레벨 에러
  EMBED_FAIL: 1101,
  HIGHLIGHT_CREATION_FAIL: 1111,
  SPOTLIGHT_CREATION_FAIL: 1112,
  MTS_CREATION_FAIL: 1113,
  GSR_RECORD_SERVER_FAIL: 1114,
  GSR_RECORD_SERVER_SPLIT_FAIL: 1115,
  GSR_SPLITTING_FAIL: 1116,
  // v2.0 AI 생성 에러
  STORY_CREATION_FAIL: 1120,
  SCRIPT_CREATION_FAIL: 1121,
  WELCOME_CREATION_FAIL: 1122,
  CONTENT_ERROR: 1123,
  VIDEO_TIMELINE_ERROR: 1124,
  VIDEO_TIMELINE_INSPECTION_ERROR: 1125,
  // 기타
  URL_UPLOAD_FAIL: 1151,
  CLIENT_UPLOAD_FAIL: 1152,
  CLIENT_UPLOAD_NETWORK: 1153,
  CLIENT_UPLOAD_TIMEOUT: 1154,
  CLIENT_UPLOAD_FORBIDDEN: 1155,
  BATCH_FORCE_FAIL: 2000,
} as const;

export const DtoProjectErrorTypeReverse = {
  // AI에서 전달 받는 에러
  1000: 'UNKNOWN_ERROR',
  1001: 'VALIDATION_ERROR',
  1002: 'AUTH_ERROR',
  1003: 'NOT_FOUND',
  1004: 'NO_DATA',
  1005: 'INDEX_DATA_NOT_FOUND',
  1006: 'YOUTUBE_DOWNLOAD_ERROR',
  1007: 'NO_STT_DATA',
  1009: 'NO_AUDIO_DATA',
  // BE 레벨 에러
  1101: 'EMBED_FAIL',
  1111: 'HIGHLIGHT_CREATION_FAIL',
  1112: 'SPOTLIGHT_CREATION_FAIL',
  1113: 'MTS_CREATION_FAIL',
  1114: 'GSR_RECORD_SERVER_FAIL',
  1115: 'GSR_RECORD_SERVER_SPLIT_FAIL',
  1116: 'GSR_SPLITTING_FAIL',
  // v2.0 AI 생성 에러
  1120: 'STORY_CREATION_FAIL',
  1121: 'SCRIPT_CREATION_FAIL',
  1122: 'WELCOME_CREATION_FAIL',
  1123: 'CONTENT_ERROR',
  1124: 'VIDEO_TIMELINE_ERROR',
  1125: 'VIDEO_TIMELINE_INSPECTION_ERROR',
  // 기타
  1151: 'URL_UPLOAD_FAIL',
  1152: 'CLIENT_UPLOAD_FAIL',
  1153: 'CLIENT_UPLOAD_NETWORK',
  1154: 'CLIENT_UPLOAD_TIMEOUT',
  1155: 'CLIENT_UPLOAD_FORBIDDEN',
  2000: 'BATCH_FORCE_FAIL',
} as const;

export type DtoProjectErrorType =
  (typeof DtoProjectErrorType)[keyof typeof DtoProjectErrorType];
export type DtoProjectErrorTypeReverse =
  (typeof DtoProjectErrorTypeReverse)[keyof typeof DtoProjectErrorTypeReverse];

export const DtoBrandTemplateType = {
  default: 0,
  normal: 1,
  shared: 2,
  common: 3,
} as const;

export const DtoBrandTemplateTypeReverse = {
  0: 'default',
  1: 'normal',
  2: 'shared',
  3: 'common',
} as const;

export type DtoBrandTemplateType =
  (typeof DtoBrandTemplateType)[keyof typeof DtoBrandTemplateType];
export type DtoBrandTemplateTypeReverse =
  (typeof DtoBrandTemplateTypeReverse)[keyof typeof DtoBrandTemplateTypeReverse];

export const DtoBrandTemplateStatus = {
  active: 0,
  inactivate: 1,
  deleted: 2,
  error: 3,
} as const;
export const DtoBrandTemplateStatusReverse = {
  0: 'active',
  1: 'inactivate',
  2: 'deleted',
  3: 'error',
} as const;

export type DtoBrandTemplateStatus =
  (typeof DtoBrandTemplateStatus)[keyof typeof DtoBrandTemplateStatus];
export type DtoBrandTemplateStatusReverse =
  (typeof DtoBrandTemplateStatusReverse)[keyof typeof DtoBrandTemplateStatusReverse];

/**
 * 보이스 프리셋 타입. BE `account_user_voice_preset.type` (VARCHAR(16) string enum) 와 매핑.
 * - SYSTEM: 시스템 시드 (모두 노출, 편집/삭제 불가, 복제만 가능)
 * - USER:   사용자 생성/복제 (CRUD/reorder 가능)
 * - SHARED: v1.1+ 워크스페이스 공유 (현 v1 미사용, enum 자리 확보)
 */
export const DtoUserVoicePresetType = {
  system: 'SYSTEM',
  user: 'USER',
  shared: 'SHARED',
} as const;

export type DtoUserVoicePresetType =
  (typeof DtoUserVoicePresetType)[keyof typeof DtoUserVoicePresetType];

export const DtoEmotion = {
  normal: 'normal',
  'shout-1': 'shout-1',
  angry: 'angry',
  'angry-1': 'angry-1',
  joy: 'joy',
  calmness: 'calmness',
  whispering: 'whispering',
} as const;

export type DtoEmotion = (typeof DtoEmotion)[keyof typeof DtoEmotion];

// 보이스 소유권 이관 메모:
// - 기존 FE 소스: DtoVoice 정적 voice catalog.
// - 새 소유자: BE /v1.0/tts/voices catalog 와 BE runtime resolver/default.
// - 런타임 계약: FE 는 voice code 를 catalog 가 제공한 문자열로만 취급한다.
// - 제거 게이트: FE static voice scan 에 static_voice_enum finding 이 남지 않음.
export type DtoVoice = string;

export const PointItemType = {
  FREE_PLAN: 0,
  PROMOTION: 1,
  PAID_PLAN: 2,
  PURCHASE: 3,
} as const;
export const PointItemReverse = {
  0: 'FREE_PLAN',
  1: 'PROMOTION',
  2: 'PAID_PLAN',
  3: 'PURCHASE',
} as const;
export type PointItemType = (typeof PointItemType)[keyof typeof PointItemType];
export type PointItemReverse =
  (typeof PointItemReverse)[keyof typeof PointItemReverse];

export const FeatureToggleType = {
  SEARCH_FROM_CHAT: 201, // "채팅으로 장면 검색"
  RENDERING_SEARCH_VIDEO: 202, // "검색으로 제작한 영상 내보내기"
  ENABLED_EDITOR: 501, // "editor 사용"
  ENABLED_ALL_FONT: 502, // "전체 폰트 사용"
  ADD_CLIP: 503, // "클립(영상) 추가"
  EDIT_BASED_TRANSCRIPT: 504, // "Transcript 기반 편집"
  EDIT_TITLE: 505, // "제목 수정"
  GENERATE_AI_TITLE: 506, // "[AI] 제목 생성"
  EDIT_TRANSCRIPT: 507, // "자막 수정"
  ADD_ASSET: 508, // "이미지/오디오 에셋 추가"
  REMOVE_WATERMARK: 601, // "워터마크 제거"
} as const;
export const FeatureToggleReverse = {
  201: 'SEARCH_FROM_CHAT',
  202: 'RENDERING_SEARCH_VIDEO',
  501: 'ENABLED_EDITOR',
  502: 'ENABLED_ALL_FONT',
  503: 'ADD_CLIP',
  504: 'EDIT_BASED_TRANSCRIPT',
  505: 'EDIT_TITLE',
  506: 'GENERATE_AI_TITLE',
  507: 'EDIT_TRANSCRIPT',
  508: 'ADD_ASSET',
  601: 'REMOVE_WATERMARK',
} as const;
export type FeatureToggleType =
  (typeof FeatureToggleType)[keyof typeof FeatureToggleType];
export type FeatureToggleReverse =
  (typeof FeatureToggleReverse)[keyof typeof FeatureToggleReverse];

export const FeatureNumericType = {
  LTS_ORIGINAL_VIDEO_SIZE: 101, // "LTS 원본 영상 용량 제한"
  LTS_ORIGINAL_VIDEO_DURATION: 102, // "LTS 원본 영상 시간 제한"
  MTS_ORIGINAL_VIDEO_SIZE: 103, // "MTS 원본 영상 용량 제한"
  MTS_ORIGINAL_VIDEO_DURATION: 104, // "MTS 원본 영상 시간 제한"
  BRAND_TEMPLATE_COUNT: 501, // "브랜드 템플릿 수"
  VIDEO_RENDERING_DATE: 701, // "내보내기 영상 보관 만료일"
  PROJECT_DATE: 702, // "프로젝트 만료 기한"
} as const;
export const FeatureNumericReverse = {
  101: 'LTS_ORIGINAL_VIDEO_SIZE',
  102: 'LTS_ORIGINAL_VIDEO_DURATION',
  103: 'MTS_ORIGINAL_VIDEO_SIZE',
  104: 'MTS_ORIGINAL_VIDEO_DURATION',
  501: 'BRAND_TEMPLATE_COUNT',
  701: 'VIDEO_RENDERING_DATE',
  702: 'PROJECT_DATE',
} as const;
export type FeatureNumericType =
  (typeof FeatureNumericType)[keyof typeof FeatureNumericType];
export type FeatureNumericReverse =
  (typeof FeatureNumericReverse)[keyof typeof FeatureNumericReverse];

export const FeatureEnumType = {
  EDIT_CUT_MODEL_TYPE: 201, // "채팅으로 컷 편집 모델"
  TTS_TYPE: 202, // "tts 선택"
  ASPECT_RATIO_TYPE: 401, // "영상 비율"
} as const;
export const FeatureEnumReverse = {
  201: 'EDIT_CUT_MODEL_TYPE',
  202: 'TTS_TYPE',
  401: 'ASPECT_RATIO_TYPE',
} as const;
export type FeatureEnumType =
  (typeof FeatureEnumType)[keyof typeof FeatureEnumType];
export type FeatureEnumReverse =
  (typeof FeatureEnumReverse)[keyof typeof FeatureEnumReverse];

// export const ProductLLMType = {
//   DEFAULT: 0,
//   GPT_4O: 100,
//   GPT_O1: 101,
//   GEMINI_3_6_FLASH: 200,
// } as const;
// export const ProductLLMReverse = {
//   0: 'DEFAULT',
//   100: 'GPT_4O',
//   101: 'GPT_O1',
//   200: 'GEMINI_3_6_FLASH',
// } as const;
// export type ProductLLMType =
//   (typeof ProductLLMType)[keyof typeof ProductLLMType];
// export type ProductLLMReverse =
//   (typeof ProductLLMReverse)[keyof typeof ProductLLMReverse];

export const ProductDiscountType = {
  STATIC: 1,
  PERCENT: 2,
} as const;
export const ProductDiscountReverse = {
  1: 'STATIC',
  2: 'PERCENT',
} as const;
export type ProductDiscountType =
  (typeof ProductDiscountType)[keyof typeof ProductDiscountType];
export type ProductDiscountReverse =
  (typeof ProductDiscountReverse)[keyof typeof ProductDiscountReverse];

// pointMap의 키는 문자열로 오므로 문자열 리터럴 사용
export const PointItemTypeSchema = z.union([
  z.literal(`${PointItemType.FREE_PLAN}`),
  z.literal(`${PointItemType.PROMOTION}`),
  z.literal(`${PointItemType.PAID_PLAN}`),
  z.literal(`${PointItemType.PURCHASE}`),
]);

// featureLimitNumericMap의 키는 문자열로 오므로 문자열 리터럴 사용
export const FeatureNumericTypeSchema = z.union([
  z.literal(`${FeatureNumericType.LTS_ORIGINAL_VIDEO_SIZE}`),
  z.literal(`${FeatureNumericType.LTS_ORIGINAL_VIDEO_DURATION}`),
  z.literal(`${FeatureNumericType.MTS_ORIGINAL_VIDEO_SIZE}`),
  z.literal(`${FeatureNumericType.MTS_ORIGINAL_VIDEO_DURATION}`),
  z.literal(`${FeatureNumericType.BRAND_TEMPLATE_COUNT}`),
  z.literal(`${FeatureNumericType.VIDEO_RENDERING_DATE}`),
  z.literal(`${FeatureNumericType.PROJECT_DATE}`),
]);

// export const FileExtensionCodeSchema = z.union([
//   z.literal(DtoExtension.mp4),
//   z.literal(DtoExtension.mov),
//   z.literal(DtoExtension.avi),
//   z.literal(DtoExtension.mkv),
//   // 10,11,12: audio
//   z.literal(DtoExtension.mp3),
//   z.literal(DtoExtension.wav),
//   z.literal(DtoExtension.aac),
//   // 20~23: image
//   z.literal(DtoExtension.jpeg),
//   z.literal(DtoExtension.png),
//   z.literal(DtoExtension.jpg),
//   z.literal(DtoExtension.gif),
//   // 30~33: font
//   z.literal(DtoExtension.ttf),
//   z.literal(DtoExtension.otf),
//   z.literal(DtoExtension.woff),
//   z.literal(DtoExtension.woff2),
//   // 100: TS
//   z.literal(DtoExtension.ts),
// ]);

// Resolution.code
// export const ResolutionCodeSchema = z.union([
//   z.literal(DtoResolution.auto), // AUTO
//   z.literal(DtoResolution['SD(480p)']), // SD
//   z.literal(DtoResolution['HD(720p)']), // HD
//   z.literal(DtoResolution['FHD(1080p)']), // FHD
//   z.literal(DtoResolution['QHD(2k)']), // QHD
//   z.literal(DtoResolution['UHD(4k)']), // UHD
// ]);

// ProductLLMType.code
// export const ProductLLMTypeCodeSchema = z.union([
//   z.literal(ProductLLMType.DEFAULT), // DEFAULT
//   z.literal(ProductLLMType.GPT_4O), // GPT-4o
//   z.literal(ProductLLMType.GPT_O1), // GPT-o1
//   z.literal(ProductLLMType.GEMINI_3_6_FLASH), // Gemini-3.6-flash
// ]);

export const ProductVendorType = {
  OPENAI: 0,
  TYPECAST: 1,
  ELEVENLABS: 2,
  GEMINI: 3,
} as const;
export const ProductVendorReverse = {
  0: 'OPENAI',
  1: 'TYPECAST',
  2: 'ELEVENLABS',
  3: 'GEMINI',
} as const;
export type ProductVendorType =
  (typeof ProductVendorType)[keyof typeof ProductVendorType];
export type ProductVendorReverse =
  (typeof ProductVendorReverse)[keyof typeof ProductVendorReverse];

export const ProductVendorTypeSchema = z.union([
  z.literal(ProductVendorType.OPENAI),
  z.literal(ProductVendorType.TYPECAST),
  z.literal(ProductVendorType.ELEVENLABS),
  z.literal(ProductVendorType.GEMINI),
]);

export const EditCutModelType = {
  DEFAULT: 0,
} as const;
export const EditCutModelReverse = {
  0: 'DEFAULT',
} as const;
export type EditCutModelType =
  (typeof EditCutModelType)[keyof typeof EditCutModelType];
export type EditCutModelReverse =
  (typeof EditCutModelReverse)[keyof typeof EditCutModelReverse];

export const EditCutModelSchema = z.union([
  z.literal(EditCutModelType.DEFAULT),
  z.literal(EditCutModelType.DEFAULT),
]);

// key: FeatureEnumType (문자열로 변환된 값)
export const FeatureLimitEnumMapSchema = z
  .object({
    [`${FeatureEnumType.TTS_TYPE}`]: z.array(ProductVendorTypeSchema), // ProductVendorType
  })
  .partial(); // 필요한 키만 내려오는 경우 대응

export const SpaceStatusType = {
  ACTIVATE: 1,
  DELETED: 2,
  STOPPED: 3,
  ERROR: 4,
} as const;
export const SpaceStatusReserve = {
  1: 'ACTIVATE',
  2: 'DELETED',
  3: 'STOPPED',
  4: 'ERROR',
} as const;
export type SpaceStatusType =
  (typeof SpaceStatusType)[keyof typeof SpaceStatusType];
export type SpaceStatusReserve =
  (typeof SpaceStatusReserve)[keyof typeof SpaceStatusReserve];

export const CancelReasonType = {
  REASON_1: 1, // 기타(직접 입력)
  REASON_2: 2, // 원하는 기능이 없어요
  REASON_3: 3, // 사용 빈도가 낮아요
  REASON_4: 4, // 가격이 부담돼요
  REASON_5: 5, // 다른 서비스를 사용하고 있어요
  REASON_6: 6, // 오류가 많아요
} as const;
export const CancelReasonReverse = {
  1: 'REASON_1',
  2: 'REASON_2',
  3: 'REASON_3',
  4: 'REASON_4',
  5: 'REASON_5',
  6: 'REASON_6',
} as const;
export type CancelReasonType =
  (typeof CancelReasonType)[keyof typeof CancelReasonType];
export type CancelReasonReverse =
  (typeof CancelReasonReverse)[keyof typeof CancelReasonReverse];

export const ProductType = {
  PLAN: 1,
  COIN: 2,
} as const;
export const ProductTypeReverse = {
  1: 'PLAN',
  2: 'COIN',
} as const;
export type ProductType = (typeof ProductType)[keyof typeof ProductType];
export type ProductTypeReverse =
  (typeof ProductTypeReverse)[keyof typeof ProductTypeReverse];

export const PaymentStatus = {
  PAYMENT_AWAITING: 0,
  PAYMENT_SUCCESS: 1,
  PAYMENT_FAILURE: -1,
  PAYMENT_REFUNDED_ALL: 2,
  PAYMENT_REFUNDED_PORTION: 3,
} as const;
export const PaymentStatusReverse = {
  0: 'PAYMENT_AWAITING',
  1: 'PAYMENT_SUCCESS',
  [-1]: 'PAYMENT_FAILURE',
  2: 'PAYMENT_REFUNDED_ALL',
  3: 'PAYMENT_REFUNDED_PORTION',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];
export type PaymentStatusReverse =
  (typeof PaymentStatusReverse)[keyof typeof PaymentStatusReverse];

export const RefundReasonType = {
  REASON_1: 1, // 기타(직접 입력)
  REASON_2: 2, // 원하는 기능이 없음
  REASON_3: 3, // 사용 빈도가 낮음
  REASON_4: 4, // 가격이 부담됨
  REASON_5: 5, // 다른 서비스를 사용 중
  REASON_6: 6, // 오류가 많아요
} as const;
export const RefundReasonReverse = {
  1: 'REASON_1',
  2: 'REASON_2',
  3: 'REASON_3',
  4: 'REASON_4',
  5: 'REASON_5',
  6: 'REASON_6',
} as const;
export type RefundReason =
  (typeof RefundReasonType)[keyof typeof RefundReasonType];
export type RefundReasonReverse =
  (typeof RefundReasonReverse)[keyof typeof RefundReasonReverse];

export const DtoVendor = {
  claude: 'claude',
  gemini: 'gemini',
  openai: 'openai',
  typecast: 'typecast',
  sonix: 'sonix',
  wakawaka: 'wakawaka',
  pyannote: 'pyannote',
  elevenlabs: 'elevenlabs',
} as const;

export type DtoVendor = (typeof DtoVendor)[keyof typeof DtoVendor];

/** 0: 금액 할인(amount), 1: 비율 할인(ratio) */
export const CouponNumberType = {
  amount: 0,
  ratio: 1,
} as const;
export const CouponNumberTypeReverse = {
  0: 'amount',
  1: 'ratio',
} as const;
export type CouponNumberType =
  (typeof CouponNumberType)[keyof typeof CouponNumberType];
export type CouponNumberTypeReverse =
  (typeof CouponNumberTypeReverse)[keyof typeof CouponNumberTypeReverse];

// 포인트 만료 타입
// 0: 무료 플랜 코인 (Free Plan Coins)
// 1: 프로모션 코인 (Promotion Coins)
// 2: 유료 플랜 코인 (Paid Plan Coins)
// 3: 구입 코인 (Purchased Coins)
// 4: 환급 코인 (Refunded Coins)
export const PointExpireType = {
  FREE_PLAN: 0,
  PROMOTION: 1,
  PAID_PLAN: 2,
  PURCHASE: 3,
  PAYBACK: 4,
} as const;

export const PointExpireTypeReverse = {
  0: 'FREE_PLAN',
  1: 'PROMOTION',
  2: 'PAID_PLAN',
  3: 'PURCHASE',
  4: 'PAYBACK',
} as const;

export type PointExpireType =
  (typeof PointExpireType)[keyof typeof PointExpireType];
export type PointExpireTypeReverse =
  (typeof PointExpireTypeReverse)[keyof typeof PointExpireTypeReverse];

/** 0: 대기중, 1: 승인, 2: 반려 */
export const SpaceMemberStatus = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
} as const;
export const SpaceMemberStatusReverse = {
  0: 'PENDING',
  1: 'APPROVED',
  2: 'REJECTED',
} as const;
export type SpaceMemberStatus =
  (typeof SpaceMemberStatus)[keyof typeof SpaceMemberStatus];
export type SpaceMemberStatusReverse =
  (typeof SpaceMemberStatusReverse)[keyof typeof SpaceMemberStatusReverse];

/** 0: 멤버, 1: 관리자, 2: 소유자 */
export const SpaceMemberRole = {
  OWNER: 0,
  ADMIN: 1,
  MEMBER: 2,
} as const;
export const SpaceMemberRoleReverse = {
  0: 'OWNER',
  1: 'ADMIN',
  2: 'MEMBER',
} as const;
export type SpaceMemberRole =
  (typeof SpaceMemberRole)[keyof typeof SpaceMemberRole];
export type SpaceMemberRoleReverse =
  (typeof SpaceMemberRoleReverse)[keyof typeof SpaceMemberRoleReverse];

// === GTS Video Status ===
/** 0: PROCESSING, 1: COMPLETED, 2: FAILED */
export const DtoGtsVideoStatus = {
  PROCESSING: 0,
  COMPLETED: 1,
  FAILED: 2,
} as const;
export const DtoGtsVideoStatusReverse = {
  0: 'processing',
  1: 'completed',
  2: 'failed',
} as const;
export type DtoGtsVideoStatus =
  (typeof DtoGtsVideoStatus)[keyof typeof DtoGtsVideoStatus];
export type DtoGtsVideoStatusReverse =
  (typeof DtoGtsVideoStatusReverse)[keyof typeof DtoGtsVideoStatusReverse];

// === Video Error Code ===
/** 영상 생성 실패 에러 코드 (AI-API VideoErrorCode enum 매칭) */
// === GTS Video Remix Mode ===
/** 0: PRESERVE (원본 보존), 1: KDUB (한글 더빙) */
export const DtoGtsVideoRemixMode = {
  PRESERVE: 0,
  KDUB: 1,
} as const;
export const DtoGtsVideoRemixModeReverse = {
  0: 'preserve',
  1: 'kdub',
} as const;
export type DtoGtsVideoRemixMode =
  (typeof DtoGtsVideoRemixMode)[keyof typeof DtoGtsVideoRemixMode];
export type DtoGtsVideoRemixModeReverse =
  (typeof DtoGtsVideoRemixModeReverse)[keyof typeof DtoGtsVideoRemixModeReverse];

// === GTS Generation Mode ===
/** 0: SHOWCASE, 1: CONCEPT, 2: SCENE, 3: CHAT, 4: UPLOAD */
export const DtoGtsGenerationMode = {
  SHOWCASE: 0,
  CONCEPT: 1,
  SCENE: 2,
  CHAT: 3,
  UPLOAD: 4,
} as const;
export const DtoGtsGenerationModeReverse = {
  0: 'showcase',
  1: 'concept',
  2: 'scene',
  3: 'chat',
  4: 'upload',
} as const;
export type DtoGtsGenerationMode =
  (typeof DtoGtsGenerationMode)[keyof typeof DtoGtsGenerationMode];
export type DtoGtsGenerationModeReverse =
  (typeof DtoGtsGenerationModeReverse)[keyof typeof DtoGtsGenerationModeReverse];

// === GTS Image Mode ===
/** 0: NORMAL, 1: STORYBOARD */
export const DtoGtsImageMode = {
  NORMAL: 0,
  STORYBOARD: 1,
} as const;
export const DtoGtsImageModeReverse = {
  0: 'normal',
  1: 'storyboard',
} as const;
export type DtoGtsImageMode =
  (typeof DtoGtsImageMode)[keyof typeof DtoGtsImageMode];
export type DtoGtsImageModeReverse =
  (typeof DtoGtsImageModeReverse)[keyof typeof DtoGtsImageModeReverse];

// === GTS Video Extend Mode ===
/** 0: PRESERVE, 1: KDUB */
export const DtoGtsVideoExtendMode = {
  PRESERVE: 0,
  KDUB: 1,
} as const;
export const DtoGtsVideoExtendModeReverse = {
  0: 'preserve',
  1: 'kdub',
} as const;
export type DtoGtsVideoExtendMode =
  (typeof DtoGtsVideoExtendMode)[keyof typeof DtoGtsVideoExtendMode];
export type DtoGtsVideoExtendModeReverse =
  (typeof DtoGtsVideoExtendModeReverse)[keyof typeof DtoGtsVideoExtendModeReverse];

// === Video Provider (범용 영상 생성 제공자) ===
/**
 * 영상 생성 제공자 코드
 * - 0: 로컬 처리 (FFMPEG)
 * - 100~199: OpenAI (Sora)
 * - 200~299: Google (Veo)
 * - 300~399: Kling
 */
export const DtoVideoProvider = {
  // 로컬 처리
  FFMPEG: 0,
  // OpenAI - Sora (100~199)
  SORA: 100,
  SORA_PRO: 101,
  // Google - Veo (200~299)
  VEO_2: 200,
  VEO_3: 201,
  VEO_3_FAST: 202,
  // Kling (300~399)
  KLING_V2: 300,
  KLING_TURBO: 301,
} as const;
export const DtoVideoProviderReverse = {
  0: 'ffmpeg',
  100: 'sora',
  101: 'sora-pro',
  200: 'veo2',
  201: 'veo3',
  202: 'veo3-fast',
  300: 'kling',
  301: 'kling-turbo',
} as const;
export type DtoVideoProvider =
  (typeof DtoVideoProvider)[keyof typeof DtoVideoProvider];
export type DtoVideoProviderReverse =
  (typeof DtoVideoProviderReverse)[keyof typeof DtoVideoProviderReverse];

// === ProjectContentType (v2.0) ===
/**
 * 프로젝트 콘텐츠 타입 (project_content.type)
 * 프로젝트 내에서 콘텐츠의 용도/역할을 정의합니다.
 *
 * 코드 범위:
 * - 0: BASE (기본/마이그레이션)
 * - 100~199: 씬 귀속 (GtsScene 참조)
 * - 200~299: 크롤링/수집 이미지
 * - 300~399: 워크플로우
 * - 400~499: 결과물
 * - 500~599: 생성 모드
 */
export const DtoProjectContentType = {
  // 기본
  BASE: 0,
  // 씬 귀속 (100~199)
  SCENE: 100,
  SCENE_PRODUCT: 110,
  SCENE_MODEL: 111,
  // 크롤링/수집 이미지 (200~299)
  MAIN: 200,
  DETAIL: 201,
  REVIEW: 202,
  UPLOADED: 203,
  PROMOTION_PRODUCT: 210,
  DEAL_PRODUCT: 211,
  // 워크플로우 (300~399)
  REFERENCE_IMAGE: 300,
  STORYBOARD: 301,
  END: 302,
  EDITED: 303,
  // 결과물 (400~499)
  MERGED: 400,
  EXTRACTED: 401,
  EXTRACTED_PRODUCT: 402,
  // 생성 모드 (500~599)
  SHOWCASE: 500,
  CONCEPT: 501,
  CHAT: 502,
  // GTS_CHAT 생성 (1000~1999)
  FIRST_FRAME_IMAGE: 1000,
} as const;

export const DtoProjectContentTypeReverse = {
  0: 'BASE',
  100: 'SCENE',
  110: 'SCENE_PRODUCT',
  111: 'SCENE_MODEL',
  200: 'MAIN',
  201: 'DETAIL',
  202: 'REVIEW',
  203: 'UPLOADED',
  210: 'PROMOTION_PRODUCT',
  211: 'DEAL_PRODUCT',
  300: 'REFERENCE_IMAGE',
  301: 'STORYBOARD',
  302: 'END',
  303: 'EDITED',
  400: 'MERGED',
  401: 'EXTRACTED',
  402: 'EXTRACTED_PRODUCT',
  500: 'SHOWCASE',
  501: 'CONCEPT',
  502: 'CHAT',
  1000: 'FIRST_FRAME_IMAGE',
} as const;

export type DtoProjectContentType =
  (typeof DtoProjectContentType)[keyof typeof DtoProjectContentType];
export type DtoProjectContentTypeReverse =
  (typeof DtoProjectContentTypeReverse)[keyof typeof DtoProjectContentTypeReverse];
